import {
  adminJobSchema,
  adminQueueSchema,
  offsetPaginationMetaSchema,
  type AdminJobView,
  type AdminQueueView,
  type JobStateName,
  type OffsetPaginationMeta,
  type QueueNameValue,
} from '@steado/admin-contract/jobs';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import type { ApiClient, QueryValue } from '@/lib/api/client';

export const JOBS_PAGE_SIZE = 20;

/**
 * Queue counts are live and go stale the moment they are read, so they refetch
 * on an interval rather than sitting until something invalidates them. Nothing
 * else in the console does this — every other screen shows stored state.
 */
export const QUEUE_REFRESH_MS = 5000;

export function queuesQueryOptions(api: ApiClient) {
  return queryOptions({
    queryKey: ['admin', 'queues'],
    queryFn: (): Promise<AdminQueueView[]> =>
      api.request({ path: '/admin/queues', schema: z.array(adminQueueSchema) }),
    refetchInterval: QUEUE_REFRESH_MS,
  });
}

export interface JobsPage {
  readonly items: AdminJobView[];
  readonly pagination: OffsetPaginationMeta;
}

const jobsPageSchema = z.object({
  items: z.array(adminJobSchema),
  pagination: offsetPaginationMetaSchema,
});

export function jobsQueryOptions(api: ApiClient, queue: QueueNameValue, state: JobStateName, offset: number) {
  const query: Record<string, QueryValue> = { state, offset, limit: JOBS_PAGE_SIZE };

  return queryOptions({
    queryKey: ['admin', 'jobs', queue, state, offset],
    queryFn: async (): Promise<JobsPage> => {
      const response = await api.requestWithMeta({
        path: `/admin/queues/${queue}/jobs`,
        query,
        schema: z.array(adminJobSchema),
      });

      return jobsPageSchema.parse({ items: response.data, pagination: response.meta.pagination });
    },
    placeholderData: (previous) => previous,
  });
}

export function jobQueryOptions(api: ApiClient, queue: QueueNameValue, jobId: string | null) {
  return queryOptions({
    queryKey: ['admin', 'job', queue, jobId],
    queryFn: (): Promise<AdminJobView> =>
      api.request({ path: `/admin/queues/${queue}/jobs/${jobId ?? ''}`, schema: adminJobSchema }),
    enabled: jobId !== null,
  });
}

export function retryJob(api: ApiClient, queue: QueueNameValue, jobId: string): Promise<AdminJobView> {
  return api.request({
    path: `/admin/queues/${queue}/jobs/${jobId}/retry`,
    method: 'POST',
    schema: adminJobSchema,
  });
}

/** 204 — the job is gone, so there is nothing to return about it. */
export function discardJob(api: ApiClient, queue: QueueNameValue, jobId: string): Promise<null> {
  return api.request({
    path: `/admin/queues/${queue}/jobs/${jobId}`,
    method: 'DELETE',
    schema: z.null(),
  });
}
